// Local-only chat persistence for sutrumai.com. Mirrors the API shape of
// the server-backed lib/chats.js so the sidebar and ChatPage don't have
// to branch — lib/chats.js dispatches between this module and the
// server based on auth state. Anonymous users get a full chat history
// kept on-device (DeepAI-style).
//
// Storage layout:
//   arthum.uid.v1                — opaque anonymous user id (one per browser)
//   arthum.chatSessionIds.v1     — array of chatIds owned by this browser
//   arthum.chat.v1.<chatId>      — { chatId, title, scope, messages, ... }

const UID_KEY    = 'arthum.uid.v1'
const INDEX_KEY  = 'arthum.chatSessionIds.v1'
const CHAT_PFX   = 'arthum.chat.v1.'

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch { return fallback }
}
function writeJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)) } catch {}
}

function rid(len = 24) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '').slice(0, len)
  }
  return (Math.random().toString(36) + Math.random().toString(36)).replace(/[^a-z0-9]/g, '').slice(0, len)
}

// Mints once per browser, persists forever. Mirrors DeepAI's
// `display_user_id` — useful as a stable handle for client-side
// dedup / analytics even though no server ever sees it.
export function getAnonUserId() {
  let id = null
  try { id = localStorage.getItem(UID_KEY) } catch {}
  if (id) return id
  id = `anon_${rid(24)}`
  try { localStorage.setItem(UID_KEY, id) } catch {}
  return id
}

function readIndex()       { return readJson(INDEX_KEY, []) }
function writeIndex(ids)   { writeJson(INDEX_KEY, ids) }
function readChat(chatId)  { return readJson(CHAT_PFX + chatId, null) }
function writeChat(chat)   { writeJson(CHAT_PFX + chat.chatId, chat) }
function removeChat(chatId) { try { localStorage.removeItem(CHAT_PFX + chatId) } catch {} }

function titleFromMessages(messages) {
  const first = Array.isArray(messages) ? messages.find((m) => m?.role === 'user' && m?.text) : null
  const txt = first?.text?.trim() || ''
  return txt.slice(0, 60) || 'New chat'
}

export function newLocalChatId() {
  return `lc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

// scope filter mirrors server semantics: legacy untagged rows surface
// under 'chat' so old sessions don't disappear after the scope feature
// landed.
export function localListChats({ scope } = {}) {
  const out = readIndex()
    .map((id) => readChat(id))
    .filter(Boolean)
    .map((c) => ({
      chatId: c.chatId,
      title: c.title,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      scope: c.scope || 'chat',
      assistantId: c.assistantId || null,
      assistantName: c.assistantName || null,
    }))
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0))
  if (!scope) return out
  return out.filter((c) => (scope === 'chat' ? c.scope !== 'assistant' : c.scope === scope))
}

export function localLoadChat(chatId) {
  return readChat(chatId)
}

// Upsert. scope/assistantId are frozen at create time — first save
// wins, later saves ignore those fields. Matches server behavior.
export function localSaveChat(chatId, { title, messages, scope, assistantId, assistantName } = {}) {
  const existing = readChat(chatId)
  const now = Date.now()
  const chat = {
    chatId,
    title: title || existing?.title || titleFromMessages(messages),
    messages: Array.isArray(messages) ? messages : (existing?.messages || []),
    scope: existing?.scope || scope || 'chat',
    assistantId: existing?.assistantId || assistantId || null,
    assistantName: existing?.assistantName || assistantName || null,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  }
  writeChat(chat)
  const ids = readIndex()
  if (!ids.includes(chatId)) writeIndex([chatId, ...ids])
  return chat
}

export function localRenameChat(chatId, title) {
  const chat = readChat(chatId)
  if (!chat) return null
  chat.title = String(title || '').trim() || chat.title
  chat.updatedAt = Date.now()
  writeChat(chat)
  return chat
}

export function localDeleteChat(chatId) {
  removeChat(chatId)
  writeIndex(readIndex().filter((id) => id !== chatId))
}

// Returns every full chat record for this browser (used by a future
// sign-in import flow — not auto-triggered).
export function getAllLocalChats() {
  return readIndex().map((id) => readChat(id)).filter(Boolean)
}

export function clearAllLocalChats() {
  for (const id of readIndex()) removeChat(id)
  writeIndex([])
}
